'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import gsap from 'gsap/all';
import { Container, getParamsByKey, Text, useTranslation, useUi } from '@weareconceptstudio/core';

import { getEvents } from '@/libs/get-data';
import createQueryString from '@/helpers/_createQueryString';
import { deepEqual } from '@/helpers/_deepEqual';

//* Styles
import EventsTemplateStyle from './style';

//* Components
const ItemEventHome = dynamic(() => import('@/modules/Home/components/Events/Item'));
const TitleAnim = dynamic(() => import('@/animation'), { ssr: false });
const RowsScrollAnim = dynamic(() => import('@/animation/trigger'), { ssr: false });
import Tag from './components/Tag';
const Pagination = dynamic(() => import('@/components/Pagination'));
const CustomMultiSelect = dynamic(() => import('@/components/CustomMultiSelect'));
const EmptySection = dynamic(() => import('@/sections/EmptySection'));
const Subscribe = dynamic(() => import('@/sections/Subscribe'));
import { getSmoother } from '@/components/ScrollWrapper/gsapSmootherInstance';
import { withDataContext } from '@/context';

const EventsTemplate = ({ pageData, globalData }) => {
	const { title } = pageData.content;
	const { selectedLang } = useTranslation();
	const { isMobile } = useUi();

	//! Next Navigation
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();

	//! Refs
	const styleRef = useRef();
	const scrollRef = useRef();
	const firstOpen = useRef();

	const titleRef = useRef();
	const filterInnerBlockRef = useRef();
	const upComingBlockRef = useRef();
	const eventsBlockRef = useRef();
	const bottomInnerBlockRef = useRef();
	const paginationRef = useRef();
	const firstLoad = useRef(true);
	const hasAutoSwitched = useRef(false);
	const initialSearchParams = useRef(searchParams);

	//! States
	const [eventsData, setEventsData] = useState();
	const [localParams, setLocalParams] = useState(searchParams);
	const [loading, setLoading] = useState(true);

	const industries = globalData?.industries ?? [];
	const options = industries.length > 0 ? industries.map(({ id, title }) => ({ id: String(id), name: title })) : [];

	const selectedIndustries = getParamsByKey(searchParams, 'industryId');

	//! Back update
	useEffect(() => {
		setLocalParams(searchParams);
	}, [searchParams]);

	//! Control Data
	useEffect(() => {
		const paramsObj = Object.fromEntries(localParams);

		getEvents({ lang: selectedLang, params: paramsObj })
			.then((data) => {
				if (!deepEqual(eventsData, data)) {
					setEventsData(data);
				}

				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setLoading(false);
			});
	}, [localParams, selectedLang]);

	//! Auto-switch to past events on first load if no upcoming events
	useEffect(() => {
		// Check if this is the first load (initial URL didn't have 'past' param) and we haven't auto-switched yet
		const wasInitialLoadWithoutPast = initialSearchParams.current.get('past') !== 'true';
		const isCurrentlyShowingUpcoming = localParams.get('past') !== 'true';
		const hasNoUpcomingEvents = eventsData?.events?.length === 0;

		if (!hasAutoSwitched.current && wasInitialLoadWithoutPast && isCurrentlyShowingUpcoming && hasNoUpcomingEvents && !loading) {
			hasAutoSwitched.current = true;
			const newParams = new URLSearchParams(localParams.toString());
			newParams.set('past', 'true');
			const newUrl = `${pathname}?${newParams.toString()}`;
			router.push(newUrl, { scroll: true });
			setLocalParams(newParams);
		}
	}, [eventsData, loading, localParams, pathname, router]);

	//! Filter
	const onFilterChange = useCallback(
		(type, typeId, isMulti = false) => {
			const newParams = new URLSearchParams(localParams.toString());

			if (isMulti) {
				const params = createQueryString({ type, typeId, searchParams: localParams });
				const newUrl = `${pathname}${params ? '?' + params : ''}`;

				router.push(newUrl, { scroll: true });
				setLocalParams(new URLSearchParams(params));
			} else {
				if (typeId) newParams.set(type, typeId);
				else newParams.delete(type);

				const newUrl = `${pathname}?${newParams.toString()}`;

				router.push(newUrl, { scroll: true });
				setLocalParams(newParams);
			}
		},
		[localParams, pathname, router]
	);

	//! Scroll to position
	useEffect(() => {
		if (styleRef.current && firstOpen.current) {
			if (isMobile) {
				window.scrollTo({
					top: 0,
					behavior: 'instant',
				});
			} else {
				const smoother = getSmoother();

				if (scrollRef.current) {
					gsap.killTweensOf(scrollRef.current);
				}

				scrollRef.current = gsap.to(smoother, {
					scrollTop: 0,
					duration: 0,
					ease: 'none',
					delay: 0,
				});
			}
		}

		return () => {
			if (eventsData) {
				firstOpen.current = true;
			}
		};
	}, [eventsData, isMobile]);

	//! News Item
	const dataEvents = useMemo(() => {
		return eventsData?.events?.map((item, index) => {
			return (
				<div key={index}>
					<ItemEventHome
						{...item}
						className={'opacity-zero'}
					/>
				</div>
			);
		});
	}, [eventsData]);

	useEffect(() => {
		return () => {
			firstLoad.current = false;
		};
	}, [localParams]);

	if (loading) return null;

	return (
		<EventsTemplateStyle>
			<TitleAnim
				animRef={titleRef}
				configAnim={{ delay: 0.5 }}
				finishPercent={0.2}
				onFinish={() => {
					filterInnerBlockRef?.current?.classList?.add('active');
					upComingBlockRef?.current?.classList?.add('active');
					eventsBlockRef?.current?.classList?.add('active');
				}}
				onBack={() => {
					filterInnerBlockRef?.current?.classList?.remove('active');
					upComingBlockRef?.current?.classList?.remove('active');
					eventsBlockRef?.current?.classList?.remove('active');
				}}
			/>

			<RowsScrollAnim
				containerRef={bottomInnerBlockRef}
				selector='.event-item-home'
				delay={firstLoad.current ? 900 : 100}
				startedAnim={eventsData?.events}
				isOpacity
			/>

			<RowsScrollAnim
				setVars={{ y: 70 }}
				toVars={{ y: 0 }}
				containerRef={paginationRef}
				perRowTrigger={false}
				selector={'.pagination-wrapper'}
			/>

			<Container isSection>
				<Text
					tag={'h1'}
					ref={titleRef}
					className={'h2 black-900 montserrat-400 title opacity-zero'}>
					{title}
				</Text>

				<Container
					row
					full
					className='top-container'>
					<div className='left-block filter-block col-4 col-dx-6 col-m-12'>
						<div
							ref={filterInnerBlockRef}
							className='filter-inner-block'>
							<CustomMultiSelect
								placeholder='industry'
								options={options}
								onChange={(values) => onFilterChange('industryId', values, true)}
								defaultSelected={selectedIndustries}
								className='filter-wrapper'
								isClearAllBtn={localParams.get('industryId')}
								onClear={() => {
									const newParams = new URLSearchParams(localParams.toString());
									newParams.delete('industryId');
									const newUrl = `${pathname}?${newParams.toString()}`;
									router.push(newUrl, { scroll: true });
									setLocalParams(newParams);
								}}
								// onClear={() => router.push(pathname, { scroll: true })}
							/>
						</div>
					</div>

					<div className='right-block filter-block col-8 col-dx-12 col-m-12'>
						<div className='filter-inner-block'>
							<Tag
								ref={upComingBlockRef}
								text={'upComingEvents'}
								isActive={(localParams.size == 0 || localParams.get('page') || localParams.get('industryId')) && localParams.get('past') !== 'true'}
								onClick={() => onFilterChange('past', '')}
							/>

							<Tag
								ref={eventsBlockRef}
								text={'events'}
								isActive={localParams.get('past') === 'true'}
								onClick={() => onFilterChange('past', 'true')}
							/>
						</div>
					</div>
				</Container>

				{eventsData?.events?.length > 0 ? (
					<div
						ref={bottomInnerBlockRef}
						className='wrapper-events'>
						{dataEvents}
					</div>
				) : (
					<EmptySection
						title='emptyEventsTitle'
						description='emptyEventsDescription'
					/>
				)}

				<div ref={paginationRef}>
					<Pagination
						searchParams={localParams}
						onCategoryChange={onFilterChange}
						total={eventsData?.total}
						pageSize={eventsData?.perPage}
					/>
				</div>

				<Subscribe className={`${!!eventsData.events ? 'active' : ''}`} />
			</Container>
		</EventsTemplateStyle>
	);
};

export default withDataContext(EventsTemplate, ['globalData']);
